import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { debtAPI, EntityDebt } from '../../api/debt';
import { supplierAPI } from '../../api/suppliers';
import { customerAPI } from '../../api/customers';
import { tripAPI } from '../../api/trips';
import { ArrowLeft, FileText } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('vi-VN').format(value);
};

interface TripDebt {
  id: number;
  date: string;
  /** Relevant value for this context: totalPurchaseValue (supplier) or totalSaleValue (customer) */
  totalValue: number;
  /** Paid amount for this party only (SUPPLIER or CUSTOMER payments) */
  paidAmount: number;
  outstandingAmount: number;
  vehicle: { licensePlate: string };
}

export const DebtDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine entity type from URL path
  const isSupplier = location.pathname.includes('/suppliers/');
  const entityType = isSupplier ? 'supplier' : 'customer';
  
  const [entityName, setEntityName] = useState('');
  const [debtSummary, setDebtSummary] = useState<EntityDebt | null>(null);
  const [trips, setTrips] = useState<TripDebt[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadData(parseInt(id));
    }
  }, [id, entityType]);

  const loadData = async (entityId: number) => {
    setLoading(true);
    try {
      // Load entity info
      if (isSupplier) {
        const supplier = await supplierAPI.getById(entityId);
        setEntityName(supplier.name);
        const debt = await debtAPI.getSupplierDebt(entityId);
        setDebtSummary(debt);
      } else {
        const customer = await customerAPI.getById(entityId);
        setEntityName(customer.name);
        const debt = await debtAPI.getCustomerDebt(entityId);
        setDebtSummary(debt);
      }

      // Load trips with debt
      const allTrips = await tripAPI.getAll({
        [isSupplier ? 'supplierId' : 'customerId']: entityId,
      });
      
      // Filter trips with outstanding amount and map to simpler structure.
      // Use totalPurchaseValue (supplier) or totalSaleValue (customer) as the relevant total.
      // Compute paidAmount per-party from payments[] to match debt-service logic.
      const partyKey = isSupplier ? 'SUPPLIER' : 'CUSTOMER';
      const tripsWithDebt = allTrips
        .filter((trip: any) => {
          // Determine per-party outstanding to decide whether to show this trip
          const relevantTotal = isSupplier
            ? parseFloat(trip.totalPurchaseValue || trip.totalValue || '0')
            : parseFloat(trip.totalSaleValue || trip.totalValue || '0');
          const partyPaid = (trip.payments || [])
            .filter((p: any) => p.paymentParty === partyKey)
            .reduce((sum: number, p: any) => sum + parseFloat(p.amount || '0'), 0);
          return relevantTotal - partyPaid > 0;
        })
        .map((trip: any) => {
          const relevantTotal = isSupplier
            ? parseFloat(trip.totalPurchaseValue || trip.totalValue || '0')
            : parseFloat(trip.totalSaleValue || trip.totalValue || '0');
          const partyPaid = (trip.payments || [])
            .filter((p: any) => p.paymentParty === partyKey)
            .reduce((sum: number, p: any) => sum + parseFloat(p.amount || '0'), 0);
          const outstanding = Math.max(relevantTotal - partyPaid, 0);
          return {
            id: trip.id,
            date: trip.date,
            totalValue: relevantTotal,
            paidAmount: partyPaid,
            outstandingAmount: outstanding,
            vehicle: trip.vehicle,
          };
        });
      
      setTrips(tripsWithDebt);
    } catch (error) {
      toast.error(t('debt.loadError'));
      navigate('/debt');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-center py-10">{t('common.loading')}</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/debt')}
          className="p-2 hover:bg-gray-100 rounded"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold">{entityName}</h1>
          <p className="text-sm text-gray-500">
            {isSupplier ? t('debt.supplierDebtDetail') : t('debt.customerDebtDetail')}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      {debtSummary && (
        <div className="grid grid-cols-4 gap-4">
          <div className="card text-center">
            <p className="text-sm text-gray-500">{t('reports.tripCount')}</p>
            <p className="text-2xl font-bold">{debtSummary.tripCount}</p>
          </div>
          <div className="card text-center">
            <p className="text-sm text-gray-500">{t('debt.totalValue')}</p>
            <p className="text-xs text-gray-400">
              ({isSupplier ? t('trips.purchasePrice') : t('trips.salePrice')})
            </p>
            <p className="text-2xl font-bold text-gray-700">
              {formatCurrency(debtSummary.totalValue)} đ
            </p>
          </div>
          <div className="card text-center">
            <p className="text-sm text-gray-500">{t('debt.paidAmount')}</p>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(debtSummary.paidAmount)} đ
            </p>
          </div>
          <div className="card text-center">
            <p className="text-sm text-gray-500">{t('debt.outstandingAmount')}</p>
            <p className={`text-2xl font-bold ${
              isSupplier ? 'text-orange-600' : 'text-blue-600'
            }`}>
              {formatCurrency(debtSummary.outstandingAmount)} đ
            </p>
          </div>
        </div>
      )}

      {/* Progress Bar */}
      {debtSummary && debtSummary.totalValue > 0 && (
        <div className="card">
          <div className="flex justify-between text-sm mb-2">
            <span>{t('debt.paymentProgress')}</span>
            <span>
              {((debtSummary.paidAmount / debtSummary.totalValue) * 100).toFixed(1)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-green-500 h-3 rounded-full transition-all"
              style={{
                width: `${Math.min((debtSummary.paidAmount / debtSummary.totalValue) * 100, 100)}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Trips with Outstanding Debt */}
      <div className="card">
        <div className="flex items-center mb-4">
          <FileText className="w-5 h-5 mr-2 text-gray-500" />
          <h2 className="text-lg font-semibold">{t('debt.tripsWithDebt')}</h2>
        </div>

        {trips.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            {t('debt.noOutstandingDebt')}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b">
              <tr className="text-left">
                <th className="pb-3 table-header">{t('reports.tripId')}</th>
                <th className="pb-3 table-header">{t('trips.date')}</th>
                <th className="pb-3 table-header">{t('trips.vehicle')}</th>
                <th className="pb-3 table-header text-right">
                  <div>{t('debt.totalValue')}</div>
                  <div className="text-xs font-normal text-gray-400">
                    ({isSupplier ? t('trips.purchasePrice') : t('trips.salePrice')})
                  </div>
                </th>
                <th className="pb-3 table-header text-right">{t('debt.paidAmount')}</th>
                <th className="pb-3 table-header text-right">{t('debt.outstandingAmount')}</th>
              </tr>
            </thead>
            <tbody>
              {trips.map((trip) => (
                <tr key={trip.id} className="border-b last:border-0">
                  <td className="py-3">
                    <button
                      onClick={() => navigate(`/trips/${trip.id}`)}
                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      title={t('debt.viewTrip')}
                    >
                      #{trip.id}
                    </button>
                  </td>
                  <td className="py-3">
                    {format(new Date(trip.date), 'dd/MM/yyyy')}
                  </td>
                  <td className="py-3">{trip.vehicle?.licensePlate || '-'}</td>
                  <td className="py-3 text-right">
                    {formatCurrency(trip.totalValue)} đ
                  </td>
                  <td className="py-3 text-right text-green-600">
                    {formatCurrency(trip.paidAmount)} đ
                  </td>
                  <td className={`py-3 text-right font-semibold ${
                    isSupplier ? 'text-orange-600' : 'text-blue-600'
                  }`}>
                    {formatCurrency(trip.outstandingAmount)} đ
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="border-t bg-gray-50">
              <tr>
                <td colSpan={3} className="py-3 font-semibold">{t('common.total')}</td>
                <td className="py-3 text-right font-semibold">
                  {formatCurrency(trips.reduce((sum, t) => sum + t.totalValue, 0))} đ
                </td>
                <td className="py-3 text-right font-semibold text-green-600">
                  {formatCurrency(trips.reduce((sum, t) => sum + t.paidAmount, 0))} đ
                </td>
                <td className={`py-3 text-right font-semibold ${
                  isSupplier ? 'text-orange-600' : 'text-blue-600'
                }`}>
                  {formatCurrency(trips.reduce((sum, t) => sum + t.outstandingAmount, 0))} đ
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>
    </div>
  );
};
